import { Outlet, NavLink, Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  GraduationCap, LayoutDashboard, Users, History, Building2,
  UserCircle, FileText, CalendarX, Wallet, LogOut, Menu, X, Award,
  ChevronDown, ChevronRight, List, BarChart3, Settings as SettingsIcon, ClipboardList, CheckCircle2, IdCard,
  HeartHandshake, Clock, ThumbsUp, AlertTriangle, ShieldAlert, User as UserIcon,
  Shield, Bell, Cog, Briefcase, BookOpen, Sparkles, PanelLeftClose, Activity, Timer,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";
import NotificationBell from "@/components/NotificationBell";
import ClassSwitcher from "@/components/ClassSwitcher";
import { getActiveClass } from "@/lib/activeClass";

/*
 * Menu structure:
 *   type: "link"  -> single item
 *   type: "group" -> collapsible with children
 */
const MENU = [
  { type: "link", to: "/app", label: "Tableau de bord", icon: LayoutDashboard, roles: ["admin", "directeur", "maitre", "inspecteur"], end: true },
  { type: "link", to: "/app/users", label: "Utilisateurs", icon: Users, roles: ["admin", "directeur"] },
  { type: "link", to: "/app/classes", label: "Mes classes", icon: Building2, roles: ["directeur"] },
  {
    type: "group",
    id: "registrations",
    label: "Demandes d'inscription",
    icon: ClipboardList,
    roles: ["admin"],
    children: [
      { to: "/app/registrations?tab=pending", label: "Nouvelles demandes", icon: Clock },
      { to: "/app/registrations?tab=approved", label: "Demandes approuvées", icon: CheckCircle2 },
      { to: "/app/registrations?tab=rejected", label: "Demandes refusées", icon: X },
      { to: "/app/registrations?tab=suspended", label: "Comptes suspendus", icon: ShieldAlert },
      { to: "/app/registrations?tab=all", label: "Tous les utilisateurs", icon: Users },
      { to: "/app/registrations?tab=stats", label: "Statistiques", icon: BarChart3 },
    ],
  },
  {
    type: "group",
    id: "activity",
    label: "Suivi des activités",
    icon: Activity,
    roles: ["admin"],
    children: [
      { to: "/app/activity", label: "Activité et statistiques", icon: BarChart3 },
      { to: "/app/history", label: "Historique des connexions", icon: History },
    ],
  },
  {
    type: "group",
    id: "students",
    label: "Gestion des élèves",
    icon: UserCircle,
    roles: ["directeur", "maitre", "inspecteur"],
    children: [
      { to: "/app/students", label: "Liste des élèves", icon: List, end: true },
      { to: "/app/students/fiches", label: "Fiches élèves", icon: IdCard },
      { to: "/app/students/stats", label: "Statistiques", icon: BarChart3 },
    ],
  },
  {
    type: "group",
    id: "exam",
    label: "Évaluations",
    icon: Award,
    roles: ["directeur", "maitre", "inspecteur"],
    children: [
      { to: "/app/evaluations?tab=config", label: "Configuration matières", icon: SettingsIcon },
      { to: "/app/evaluations?tab=students", label: "Candidats", icon: Users },
      { to: "/app/evaluations?tab=notes", label: "Notes", icon: ClipboardList },
      { to: "/app/evaluations?tab=results", label: "Résultats", icon: CheckCircle2 },
      { to: "/app/evaluations?tab=bulletins", label: "Bulletin", icon: FileText },
    ],
  },
  {
    type: "group",
    id: "vie-scolaire",
    label: "Vie scolaire",
    icon: HeartHandshake,
    roles: ["directeur", "maitre", "inspecteur"],
    children: [
      { to: "/app/assiduite?tab=appel", label: "Appel et présences", icon: CheckCircle2 },
      { to: "/app/assiduite?tab=absences", label: "Absences et retards", icon: CalendarX },
      { to: "/app/assiduite?tab=stats", label: "Statistiques d'assiduité", icon: BarChart3 },
      { to: "/app/vie-scolaire?tab=comportement", label: "Comportement", icon: ThumbsUp },
      { to: "/app/vie-scolaire?tab=difficultes", label: "Difficultés", icon: AlertTriangle },
      { to: "/app/vie-scolaire?tab=recompenses", label: "Récompenses", icon: Award },
      { to: "/app/vie-scolaire?tab=sanctions", label: "Sanctions", icon: ShieldAlert },
      { to: "/app/vie-scolaire?tab=fiche", label: "Fiche de suivi", icon: UserIcon },
    ],
  },
  {
    type: "group",
    id: "finance",
    label: "Finance",
    icon: Wallet,
    roles: ["directeur", "maitre"],
    children: [
      { to: "/app/finance?tab=dashboard", label: "Tableau de bord", icon: BarChart3 },
      { to: "/app/finance?tab=config", label: "Paramètres", icon: SettingsIcon },
      { to: "/app/finance?tab=inscriptions", label: "Inscriptions", icon: UserCircle },
      { to: "/app/finance?tab=mensualites", label: "Mensualités", icon: ClipboardList },
      { to: "/app/finance?tab=cotisations", label: "Cotisations", icon: FileText },
      { to: "/app/finance?tab=paiements", label: "Paiements", icon: CheckCircle2 },
      { to: "/app/finance?tab=depenses", label: "Dépenses", icon: List },
    ],
  },
  {
    type: "group",
    id: "espace",
    label: "Espace enseignants",
    icon: Briefcase,
    roles: ["directeur", "maitre"],
    children: [
      { to: "/app/espace-enseignants?tab=infos", label: "Infos perso", icon: UserIcon },
      { to: "/app/espace-enseignants?tab=documentation", label: "Documentation", icon: BookOpen },
      { to: "/app/espace-enseignants?tab=horaire", label: "Quantum horaire", icon: Timer },
      { to: "/app/espace-enseignants?tab=fiche", label: "Génération de fiche", icon: Sparkles },
    ],
  },
  {
    type: "group",
    id: "settings",
    label: "Paramètres",
    icon: Cog,
    roles: ["admin", "directeur", "maitre", "inspecteur"],
    children: [
      { to: "/app/settings?tab=profile", label: "Mon profil", icon: UserIcon },
      { to: "/app/settings?tab=school", label: "Mon école", icon: Building2, cond: "school" },
      { to: "/app/settings?tab=security", label: "Sécurité", icon: Shield },
      { to: "/app/settings?tab=notifications", label: "Notifications", icon: Bell },
      { to: "/app/espace-enseignants?tab=documentation", label: "Documentation enseignants", icon: BookOpen, roles: ["admin"] },
      { to: "/app/settings?tab=danger", label: "Supprimer mon compte", icon: AlertTriangle },
    ],
  },
];

const ROLE_LABELS = {
  admin: "Admin",
  directeur: "Directeur",
  maitre: "Maître",
  inspecteur: "Inspecteur",
};

function MenuGroup({ item, currentPath, currentSearch, onNav }) {
  const activeChild = item.children.find((c) => {
    const [p, q] = c.to.split("?");
    if (p !== currentPath) return false;
    if (q) {
      const target = new URLSearchParams(q).get("tab");
      const cur = new URLSearchParams(currentSearch).get("tab");
      return target === cur;
    }
    return true;
  });
  const [open, setOpen] = useState(!!activeChild);
  useEffect(() => { if (activeChild) setOpen(true); }, [activeChild]);

  const Icon = item.icon;
  return (
    <div className="space-y-0.5">
      <button
        onClick={() => setOpen(!open)}
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
          activeChild ? "text-[#1F7A63]" : "text-[#1A2E28] hover:bg-slate-50"
        }`}
        data-testid={`group-${item.id}`}
      >
        <Icon className="w-4 h-4 shrink-0" />
        <span className="flex-1 text-left">{item.label}</span>
        {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
      </button>
      {open && (
        <div className="ml-3 pl-3 border-l-2 border-[#E2E8F0] space-y-0.5">
          {item.children.map((c) => {
            const [path, query] = c.to.split("?");
            const isActive = path === currentPath && (
              !query || new URLSearchParams(query).get("tab") === new URLSearchParams(currentSearch).get("tab")
            );
            const ChildIcon = c.icon;
            return (
              <button
                key={c.to}
                onClick={() => onNav(c.to)}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors text-left ${
                  isActive ? "bg-[#E8F2EF] text-[#1F7A63] font-semibold" : "text-[#5C736A] hover:bg-slate-50 hover:text-[#1A2E28]"
                }`}
                data-testid={`sub-${c.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                {ChildIcon && <ChildIcon className="w-3.5 h-3.5 shrink-0" />}
                <span>{c.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [openSidebar, setOpenSidebar] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("ek_sidebar_collapsed") === "1");
  const [activeClassKey, setActiveClassKey] = useState(getActiveClass());
  useEffect(() => {
    const on = () => setActiveClassKey(getActiveClass());
    window.addEventListener("ek-active-class", on);
    return () => window.removeEventListener("ek-active-class", on);
  }, []);
  const [schoolAccess, setSchoolAccess] = useState(null);

  useEffect(() => { localStorage.setItem("ek_sidebar_collapsed", collapsed ? "1" : "0"); }, [collapsed]);

  useEffect(() => {
    if (!user) return;
    import("@/lib/api").then(({ api }) => {
      api.get("/settings/school-access")
        .then(({ data }) => setSchoolAccess(data))
        .catch(() => setSchoolAccess({ can_view: false }));
    });
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const items = MENU.filter((m) => {
    if (!m.roles.includes(user?.role)) return false;
    if (m.to === "/app/school" && user?.role === "maitre" && !user?.is_independent) return false;
    if (m.to === "/app/finance" && user?.role === "maitre" && !user?.is_independent) return false;
    if (m.id === "finance" && user?.role === "maitre" && !user?.is_independent) return false;
    return true;
  });

  const itemsWithLabel = items.map((m) => {
    if (m.to === "/app/users" && user?.role === "directeur") return { ...m, label: "Mes maîtres" };
    if (m.type === "group") {
      return {
        ...m,
        children: m.children.filter((c) => (!c.roles || c.roles.includes(user?.role)) && (c.cond !== "school" || schoolAccess?.can_view === true)),
      };
    }
    return m;
  });

  const nav = (to) => {
    setOpenSidebar(false);
    navigate(to);
  };

  const renderMenu = () => itemsWithLabel.map((item) => {
    if (item.type === "group") {
      return (
        <MenuGroup
          key={item.id}
          item={item}
          currentPath={location.pathname}
          currentSearch={location.search}
          onNav={nav}
        />
      );
    }
    const Icon = item.icon;
    return (
      <NavLink
        key={item.to}
        to={item.to}
        end={item.end}
        onClick={() => setOpenSidebar(false)}
        data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
        className={({ isActive }) =>
          `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
            isActive ? "bg-[#E8F2EF] text-[#1F7A63]" : "text-[#1A2E28] hover:bg-slate-50"
          }`
        }
      >
        <Icon className="w-4 h-4 shrink-0" />
        <span>{item.label}</span>
      </NavLink>
    );
  });

  return (
    <div className="min-h-screen bg-[#F6F8F9] flex">
      {/* Sidebar desktop */}
      <aside className={`hidden md:flex bg-white border-r border-[#E2E8F0] flex-col sticky top-0 h-screen shrink-0 overflow-hidden transition-[width,opacity] duration-300 ${collapsed ? "md:w-0 opacity-0 border-r-0" : "md:w-64 opacity-100"}`} data-testid="desktop-sidebar" aria-hidden={collapsed}>
        <div className="h-16 px-5 flex items-center justify-between border-b border-[#E2E8F0] shrink-0 w-64">
          <Link to="/app" className="flex items-center gap-2" data-testid="sidebar-logo">
            <div className="w-9 h-9 rounded-lg bg-[#1F7A63] grid place-items-center">
              <GraduationCap className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-lg" style={{ fontFamily: "Outfit" }}>EDUKATI</span>
          </Link>
          <button onClick={() => setCollapsed(true)} className="p-1.5 rounded-md text-[#5C736A] hover:bg-slate-100" title="Masquer le menu" data-testid="sidebar-collapse-btn">
            <PanelLeftClose className="w-5 h-5" />
          </button>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto w-64">{renderMenu()}</nav>
        <div className="p-3 border-t border-[#E2E8F0] shrink-0 w-64">
          <div className="px-3 py-2 mb-2 rounded-lg bg-slate-50">
            <div className="text-xs text-[#5C736A]">Connecté en tant que</div>
            <div className="text-sm font-semibold truncate">{user?.name}</div>
            <div className="text-xs text-[#1F7A63] font-semibold">{ROLE_LABELS[user?.role]}</div>
          </div>
          <Button variant="outline" data-testid="logout-btn" onClick={handleLogout} className="w-full justify-start gap-2 text-[#1A2E28]">
            <LogOut className="w-4 h-4" /> Déconnexion
          </Button>
        </div>
      </aside>

      {/* Sidebar mobile */}
      {openSidebar && (
        <div className="md:hidden fixed inset-0 z-50 flex" onClick={() => setOpenSidebar(false)}>
          <div className="absolute inset-0 bg-black/40" />
          <div className="relative w-72 bg-white h-full flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-[#E2E8F0]">
              <span className="font-bold text-lg" style={{ fontFamily: "Outfit" }}>EDUKATI</span>
              <button onClick={() => setOpenSidebar(false)}><X className="w-5 h-5" /></button>
            </div>
            <nav className="flex-1 p-3 space-y-1 overflow-y-auto">{renderMenu()}</nav>
            <div className="p-3 border-t border-[#E2E8F0]">
              <button onClick={handleLogout} className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-red-600">
                <LogOut className="w-4 h-4" /> Déconnexion
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-white border-b border-[#E2E8F0] flex items-center justify-between px-4 sm:px-8 sticky top-0 z-20">
          <div className="flex items-center gap-3">
            <button className="md:hidden" onClick={() => setOpenSidebar(true)} data-testid="mobile-menu-btn">
              <Menu className="w-6 h-6" />
            </button>
            {collapsed && (
              <button className="hidden md:inline-flex p-1.5 rounded-md hover:bg-slate-100" onClick={() => setCollapsed(false)} title="Afficher le menu" data-testid="sidebar-expand-btn">
                <Menu className="w-6 h-6" />
              </button>
            )}
            <div className="hidden md:block text-sm text-[#5C736A]">
              Année scolaire <span className="font-semibold text-[#1A2E28]">2025-2026</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <NotificationBell />
            <span className="text-sm hidden sm:inline">{user?.name}</span>
            {user?.avatar_base64 ? (
              <img src={user.avatar_base64} alt="avatar" className="w-9 h-9 rounded-full object-cover border border-[#E2E8F0]" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-[#1F7A63] text-white grid place-items-center text-sm font-bold">
                {(user?.name || "?").slice(0, 1).toUpperCase()}
              </div>
            )}
          </div>
        </header>
        {user?.role === "maitre" && <ClassSwitcher />}
        <main className="flex-1 p-4 sm:p-8 overflow-x-hidden">
          <Outlet key={activeClassKey} />
        </main>
      </div>
    </div>
  );
}
